import { useEffect, useState } from "react";
import { Minus, Plus, RotateCcw, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as approvalsApi from "@/lib/approvalsApi";

/** Owner-facing names for the action types the AI assistant can stage. */
export const TYPE_LABELS = {
  bill_bundle: "Bill + new items",
  purchase_bill: "Purchase bill",
  create_stock_items: "New stock items",
  create_stock_item: "New stock item",
  update_stock_item: "Stock item change",
  create_supplier: "New supplier",
  update_supplier: "Supplier change",
  stock_movement: "Stock movement",
  expense: "Expense",
  register_entry: "Register entry",
};
export const typeLabel = type => TYPE_LABELS[type] || String(type || "").replaceAll("_", " ");

export const STATUS_TONE = {
  pending: "bg-warning-subtle text-warning",
  approved: "bg-success-subtle text-success",
  rejected: "bg-danger-subtle text-danger",
  expired: "bg-muted text-muted-foreground",
  failed: "bg-danger-subtle text-danger",
};

/** "3 h ago" for the list; anything older than a day reads in days. */
export function ageText(iso, now = Date.now()) {
  if (!iso) return "";
  const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

export const num = value => (value === "" || value === null || value === undefined ? "" : Number(value));
export const money = value => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The bill photo(s) of an action, zoomable (owner brief 2026-11-04). The file needs the
 * login on its request, so it is fetched as a blob. A PDF is shown in a frame.
 */
export function BillPhotos({ actionId, attachmentIds = [], token }) {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const key = attachmentIds.join(",");

  useEffect(() => {
    let alive = true;
    const urls = [];
    setFiles([]);
    setError("");
    Promise.all(attachmentIds.map(id => approvalsApi.attachmentUrl(actionId, id, token)))
      .then(done => {
        done.forEach(f => urls.push(f.url));
        if (alive) setFiles(done);
      })
      .catch(failure => alive && setError(approvalsApi.errorText(failure)));
    return () => {
      alive = false;
      urls.forEach(u => URL.revokeObjectURL?.(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, key, token]);

  if (!attachmentIds.length) {
    return <div className="rounded-md border p-4 text-meta text-muted-foreground" data-testid="bill-photos-none">No bill photo was attached to this request.</div>;
  }
  if (error) {
    return <div className="rounded-md border border-danger/30 bg-danger-subtle p-4 text-meta text-danger" data-testid="bill-photos-error">The bill photo could not be opened: {error}</div>;
  }
  const file = files[index];
  const isPdf = file?.type === "application/pdf";
  return (
    <div className="flex flex-col gap-2 min-h-0 h-full" data-testid="bill-photos">
      <div className="flex items-center gap-2 shrink-0">
        {files.length > 1 && files.map((_, i) => (
          <Button key={i} size="sm" variant={i === index ? "default" : "outline"} onClick={() => setIndex(i)}>Page {i + 1}</Button>
        ))}
        {!isPdf && (
          <div className="ml-auto flex items-center gap-1">
            <Button size="icon" variant="outline" aria-label="Zoom out" onClick={() => setZoom(z => Math.max(1, +(z - 0.5).toFixed(1)))} data-testid="zoom-out"><Minus /></Button>
            <span className="w-12 text-center text-meta tabular-nums" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
            <Button size="icon" variant="outline" aria-label="Zoom in" onClick={() => setZoom(z => Math.min(4, +(z + 0.5).toFixed(1)))} data-testid="zoom-in"><Plus /></Button>
            <Button size="icon" variant="outline" aria-label="Reset zoom" onClick={() => setZoom(1)}><RotateCcw /></Button>
          </div>
        )}
      </div>
      <div className="flex-1 min-h-[24rem] overflow-auto rounded-md border bg-muted/40">
        {!file ? (
          <div className="p-6 text-meta text-muted-foreground">Opening the bill…</div>
        ) : isPdf ? (
          <iframe title="Bill" src={file.url} className="w-full h-full min-h-[32rem]" />
        ) : (
          <img src={file.url} alt={`Bill photo ${index + 1}`} style={{ width: `${zoom * 100}%`, maxWidth: "none" }} className="block" data-testid="bill-photo" />
        )}
      </div>
      {isPdf && <p className="text-micro text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> PDF bill</p>}
    </div>
  );
}

export function FlagList({ flags = [] }) {
  if (!flags.length) return null;
  return (
    <ul className="space-y-1" data-testid="action-flags">
      {flags.map((f, i) => (
        <li key={i} className={`rounded-md px-3 py-2 text-meta ${f.level === "error" ? "bg-danger-subtle text-danger" : "bg-warning-subtle text-warning"}`}>
          <span className="font-semibold mr-1">{String(f.code || "").replaceAll("_", " ")}</span>{f.message}
        </li>
      ))}
    </ul>
  );
}

export const FIELD = "h-9 w-full rounded-md border border-input bg-background px-2 text-body disabled:opacity-60";
